// OP-3605 — Tela da remessa (ver ESL1; agir ESL4): um bloco de ação por
// status — entrega (DESPACHADA), coleta por viagem (ENTREGUE), recebimento
// (EM_RETORNO) e conferência peça a peça (EM_CONFERENCIA).
import { FC, useMemo, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useEstoqueAnexos, useEstoqueRemessaDetalhe } from '../../../../hooks/estoque'
import { usePerfilEstoque } from '../../../../hooks/estoque/perfil'
import {
  cancelarEstoqueRemessa, coletarEstoqueRemessa, conferirEstoqueRemessa, confirmarEntregaEstoqueRemessa, createEstoqueAnexoUrl, descartarEstoqueAnexo, receberEstoqueRemessa, saveEstoqueAnexo,
} from '../../../../Service/estoque'
import { CicloVida, DecisaoConferencia, rotuloMetodoEnvio, RemessaItem, StatusRemessa } from '../../../../types/estoque'
import {
  BD, SEVERIDADE, Button, Card, Field, Input, ListaCartoes, PageSubtitle, PageTitle, Select, SoCelular, SoDesktop, Spinner, StatusBadge, TabelaWrap, Table,
} from '../../styles'
import { fmtDate, fmtDateTime } from '../../statusUi'
import { AnexarArquivo } from '../AnexarArquivo'
import { exibirDocumento, impedimentoDocumento, mascaraDocumento } from '../documento'
import { BarraFixa } from '../BarraFixa'
import { LinhaQuantidade, ListaMarcacao } from '../Contagem'
import { tratarErro } from '../erros'
import { RetornoLeitura, acharPorCodigo } from '../leitura'
import { LeitorContinuo } from '../LeitorContinuo'
import { decisoesDe, rotuloDecisao } from '../conferencia'
import { CICLO_UI } from '../Equipamento/regras'
import { prazoUi } from '../formato'
import { Stepper } from '../Stepper'
import { hojeISO } from '../Solicitar/form'
import {
  ColetaForm, LinhaConferencia, ROTULO_ACAO, STATUS_REMESSA_UI, acaoDoStatus, ehColetavel, emCampo, fraseDesfecho, impedimentoCancelarRemessa, impedimentoColeta, podeCancelarRemessa,
  impedimentoConferencia, linhasColetaveis, linhasDaViagem, linhasDeConsumo, linhasPerdidas, montarConferencia, restanteEmCampo,
} from './regras'
import { LOCAL_EXECUTION } from '../../../../utils/featureFlag'

const Bloco: FC<{ titulo: string; children: any }> = ({ titulo, children }) => (
  <Card style={{ padding: 16, marginBottom: 16 }}><div style={{ fontWeight: 600, marginBottom: 8 }}>{titulo}</div>{children}</Card>
)

export const EstoqueRemessa: FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const idNum = Number(id) || null
  const hoje = useMemo(hojeISO, [])
  const { pode } = usePerfilEstoque()
  const age = pode('REMESSAS')
  // O comprovante sobe por duas rotas de ESL18. Sem a capacidade, o controle
  // não aparece — ele é opcional aqui, então a ação continua disponível.
  const podeAnexar = pode('ANEXAR')
  const { data: r, isLoading, isError, isUnauthorized, mutate } = useEstoqueRemessaDetalhe(idNum)
  const fotosSeparacaoQ = useEstoqueAnexos('SOLICITACAO', r?.solicitacaoId ?? null, 'SEPARACAO')
  const despachoQ = useEstoqueAnexos('SOLICITACAO', r?.solicitacaoId ?? null, 'DESPACHO')
  const entregaQ = useEstoqueAnexos('REMESSA', idNum, 'ENTREGA')
  const [busy, setBusy] = useState(false)
  const [entrega, setEntrega] = useState({ entregueEm: hoje, recebidoPor: '', recebidoDocumento: '', comprovanteKey: null as string | null })
  const [coleta, setColeta] = useState<ColetaForm>({ quantidades: {}, novaPrevisao: '' })
  const [conf, setConf] = useState<Record<number, LinhaConferencia>>({})
  const [desfecho, setDesfecho] = useState<string | null>(null)
  const [confirmandoCancelar, setConfirmandoCancelar] = useState(false)
  const [motivoCancelamento, setMotivoCancelamento] = useState('')

  if (isUnauthorized && !LOCAL_EXECUTION) return <Navigate to="/auth" replace />
  if (isLoading) return <Card><div style={{ textAlign: 'center', padding: 24 }}><Spinner /></div></Card>
  if (isError || !r) {
    return <><PageTitle>Remessa</PageTitle><Card><div style={{ padding: 24, color: BD.grayText }}>Remessa não encontrada ou fora do seu escopo. <Button $variant="secondary" onClick={() => navigate('/interno/estoque/remessas')}>Voltar</Button></div></Card></>
  }

  const executar = async (acao: () => Promise<any>, ok: string) => {
    setBusy(true)
    try {
      const res = await acao()
      toast.success(ok)
      await mutate()
      return res
    } catch (err) {
      const e = tratarErro(err)
      if (e.tipo === 'sessao' && !LOCAL_EXECUTION) { navigate('/auth'); return null }
      toast.error(e.texto)
      if (e.tipo === 'estado') await mutate()
      return null
    } finally {
      setBusy(false)
    }
  }

  const status = r.status as StatusRemessa
  const ui = STATUS_REMESSA_UI[status] ?? { bg: '#F3F4F6', color: '#374151', label: r.status }
  const acao = acaoDoStatus(status)
  // Três recortes distintos da mesma remessa, cada um com a régua do servidor:
  // o que pode voltar numa coleta, o que ESTA viagem trouxe (é o que a
  // conferência decide) e o consumo, que não volta nem se confere.
  const paraColetar = linhasColetaveis(r.itens)
  const pecasParaColetar = paraColetar.filter((l) => l.controle === 'SERIAL')
  const consumiveisParaColetar = paraColetar.filter((l) => l.controle !== 'SERIAL')
  const pecasMarcadas = pecasParaColetar.filter((l) => (coleta.quantidades[l.id] || 0) > 0).length
  const daViagem = linhasDaViagem(r.itens)
  const consumo = linhasDeConsumo(r.itens)
  const perdidas = linhasPerdidas(r.itens)
  const fotosSeparacao = fotosSeparacaoQ.data
  const comprovanteDespacho = r.comprovanteDespachoUrl ? despachoQ.data.find((a) => a.chave === r.comprovanteDespachoUrl) ?? null : null
  const comprovanteEntrega = r.comprovanteEntregaUrl ? [...entregaQ.data, ...despachoQ.data].find((a) => a.chave === r.comprovanteEntregaUrl) ?? null : null

  const trocarComprovante = async () => {
    const key = entrega.comprovanteKey
    setEntrega((e) => ({ ...e, comprovanteKey: null }))
    if (!key) return
    setBusy(true)
    try {
      await descartarEstoqueAnexo('REMESSA', r.id, key)
    } catch (err) {
      const e = tratarErro(err)
      if (e.tipo === 'sessao' && !LOCAL_EXECUTION) { navigate('/auth'); return }
      toast.info('O comprovante anterior continua nos anexos da remessa.')
    } finally {
      setBusy(false)
    }
  }

  const enviarComprovante = async (comprovanteFile: File) => {
    setBusy(true)
    try {
      const { data } = await createEstoqueAnexoUrl('REMESSA', r.id, comprovanteFile.type)
      const put = await fetch(data.data.url, { method: 'PUT', headers: { 'Content-Type': comprovanteFile.type }, body: comprovanteFile })
      if (!put.ok) throw new Error(`PUT ${put.status}`)
      await saveEstoqueAnexo({ refTipo: 'REMESSA', refId: r.id, escopo: 'ENTREGA', key: data.data.key, nome: comprovanteFile.name, mime: comprovanteFile.type, bytes: comprovanteFile.size })
      setEntrega((e) => ({ ...e, comprovanteKey: data.data.key }))
      toast.success('Comprovante enviado')
    } catch (err) {
      const e = tratarErro(err)
      if (e.tipo === 'sessao' && !LOCAL_EXECUTION) { navigate('/auth'); return }
      toast.error(e.texto)
    } finally {
      setBusy(false)
    }
  }

  const retorno = prazoUi(r.prorrogadoPara ?? r.previsaoRetorno, undefined, 'Retorno')
  const emCurso = ['DESPACHADA', 'ENTREGUE', 'EM_RETORNO', 'EM_CONFERENCIA'].indexOf(status) >= 0
  const impColeta = impedimentoColeta(paraColetar, coleta, hoje)
  const sobra = restanteEmCampo(paraColetar, coleta.quantidades)
  const impConf = impedimentoConferencia(daViagem, conf)
  const setLinhaConf = (l: RemessaItem, patch: Partial<LinhaConferencia>) =>
    setConf((c) => ({ ...c, [l.id]: { ...(c[l.id] || { decisao: '', quantidadeRetornada: '', observacao: '' }), ...patch } }))

  const lerColeta = (codigo: string): RetornoLeitura => {
    const l = acharPorCodigo(pecasParaColetar, codigo, (x) => [x.serial, x.patrimonio])
    if (!l) return { texto: `${codigo} não está em campo nesta remessa.`, tom: 'erro' }
    if ((coleta.quantidades[l.id] || 0) > 0) return { texto: `${l.serial ?? codigo} já estava marcada para voltar.`, tom: 'aviso' }
    setColeta((c) => ({ ...c, quantidades: { ...c.quantidades, [l.id]: 1 } }))
    return { texto: `✓ ${l.serial ?? codigo} · ${l.itemNome} volta agora`, tom: 'ok' }
  }

  const lerConferencia = (codigo: string): RetornoLeitura => {
    const l = acharPorCodigo(daViagem.filter((x) => x.controle === 'SERIAL'), codigo, (x) => [x.serial, x.patrimonio])
    if (!l) return { texto: `${codigo} não veio nesta viagem.`, tom: 'erro' }
    const atual = conf[l.id]?.decisao
    if (atual) return { texto: `${l.serial ?? codigo} já conferida: ${rotuloDecisao('RETORNO', atual)}.`, tom: 'aviso' }
    setLinhaConf(l, { decisao: 'OK' })
    return { texto: `✓ ${l.serial ?? codigo} · ${rotuloDecisao('RETORNO', 'OK')}. Se estiver avariada, troque na lista.`, tom: 'ok' }
  }

  const confirmarEntrega = () => executar(
    () => confirmarEntregaEstoqueRemessa({ id: r.id, entregueEm: entrega.entregueEm, recebidoPor: entrega.recebidoPor.trim() || null, recebidoDocumento: entrega.recebidoDocumento.trim() || null, comprovanteUrl: entrega.comprovanteKey }),
    'Entrega confirmada',
  )

  const registrarColeta = () => executar(() => coletarEstoqueRemessa({
    id: r.id,
    itens: paraColetar.filter((l) => (coleta.quantidades[l.id] || 0) > 0).map((l) => ({ id: l.id, quantidade: coleta.quantidades[l.id] })),
    novaPrevisao: sobra > 0 ? coleta.novaPrevisao : null,
  }), sobra > 0 ? 'Coleta registrada; a remessa continua com equipamento em campo' : 'Coleta registrada; tudo em retorno')
    .then(() => setColeta({ quantidades: {}, novaPrevisao: '' }))

  const confirmarRecebimento = () => executar(() => receberEstoqueRemessa({ id: r.id }), 'Recebimento registrado')

  const desarmarCancelamento = () => { setConfirmandoCancelar(false); setMotivoCancelamento('') }
  const cancelarRemessa = async () => {
    const res = await executar(() => cancelarEstoqueRemessa(r.id, motivoCancelamento.trim()), 'Remessa cancelada; o pedido voltou para Pronta')
    if (res) desarmarCancelamento()
  }
  const impCancelar = impedimentoCancelarRemessa(motivoCancelamento)

  const fecharConferencia = async () => {
    const res = await executar(() => conferirEstoqueRemessa({ id: r.id, itens: montarConferencia(daViagem, conf) }), 'Conferência registrada')
    const d = res?.data?.data
    // Decisão desta viagem não se reaproveita na próxima: uma remessa
    // com peça ainda em campo volta a ENTREGUE e coleta de novo.
    if (res) setConf({})
    if (d && d.pecas != null) setDesfecho(fraseDesfecho(Number(d.pecas), Number(d.pecasRetornadas), Number(d.pecasPerdidas)))
  }

  const ACOES: Record<string, { rodar: () => void; impedimento: string | null }> = {
    ENTREGA: { rodar: confirmarEntrega, impedimento: entrega.entregueEm ? impedimentoDocumento(entrega.recebidoDocumento, false) : 'Informe quando o equipamento chegou.' },
    COLETA: { rodar: registrarColeta, impedimento: impColeta },
    RECEBIMENTO: { rodar: confirmarRecebimento, impedimento: null },
    CONFERENCIA: { rodar: fecharConferencia, impedimento: impConf },
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <PageTitle>{r.codigo} <StatusBadge $bg={ui.bg} $color={ui.color}>{ui.label}</StatusBadge></PageTitle>
          <SoDesktop>
            <PageSubtitle>
              {r.detentorNome} · de {r.localOrigemNome} · {rotuloMetodoEnvio(r.metodoEnvio)}{r.transportadora ? ` (${r.transportadora})` : ''}{r.codigoRastreio ? ` · rastreio ${r.codigoRastreio}` : ''}
              {' · '}despachada {fmtDateTime(r.despachadoEm)} · entregue {fmtDate(r.entregueEm)} · retorno previsto {fmtDate(r.prorrogadoPara ?? r.previsaoRetorno)}
              {r.solicitacaoId && <> · <a href={`/interno/estoque/fila/${r.solicitacaoId}`} onClick={(e) => { e.preventDefault(); navigate(`/interno/estoque/fila/${r.solicitacaoId}`) }}>ver pedido</a></>}
            </PageSubtitle>
          </SoDesktop>
          <SoCelular>
            <PageSubtitle>
              {r.detentorNome} · de {r.localOrigemNome}
              <br />{rotuloMetodoEnvio(r.metodoEnvio, 'sem método')}{r.transportadora ? ` (${r.transportadora})` : ''}{r.codigoRastreio ? ` · rastreio ${r.codigoRastreio}` : ''} · entregue {fmtDate(r.entregueEm)}
              {r.solicitacaoId && <> · <a href={`/interno/estoque/fila/${r.solicitacaoId}`} onClick={(e) => { e.preventDefault(); navigate(`/interno/estoque/fila/${r.solicitacaoId}`) }}>ver pedido</a></>}
            </PageSubtitle>
          </SoCelular>
        </div>
        <Button $variant="secondary" onClick={() => navigate('/interno/estoque/remessas')}>‹ Remessas</Button>
      </div>

      <SoCelular>
        {emCurso && (
          <Card style={{ padding: 12, marginBottom: 16, borderColor: retorno.alerta ? SEVERIDADE.atencao.borda : undefined, background: retorno.alerta ? SEVERIDADE.atencao.bg : undefined }}>
            <div style={{ fontWeight: 600, color: retorno.alerta ? SEVERIDADE.atencao.texto : BD.black }}>
              {retorno.texto}{r.prorrogadoPara ? ' (prorrogada)' : ''}
            </div>
            <div style={{ fontSize: 12, color: BD.grayText }}>{r.pecas} em campo · {r.pecasRetornadas} já voltaram</div>
          </Card>
        )}
      </SoCelular>

      <Bloco titulo={`Equipamento · ${r.pecasRetornadas} voltaram · ${r.pecas} em campo${r.pecasPerdidas ? ` · ${r.pecasPerdidas} não retornaram` : ''}`}>
        <TabelaWrap>
          <Table>
            <thead><tr><th>Item</th><th>Serial</th><th style={{ textAlign: 'right' }}>Saiu</th><th style={{ textAlign: 'right' }}>Voltou</th><th style={{ textAlign: 'right' }}>Em campo</th><th>Retorno</th></tr></thead>
            <tbody>
              {r.itens.filter((l) => l.unidadeId != null).map((l) => (
                <tr key={l.id}>
                  <td>{l.itemNome}</td><td style={{ fontFamily: 'monospace' }}>{l.serial ?? '—'}</td>
                  <td style={{ textAlign: 'right' }}>{l.quantidade}</td><td style={{ textAlign: 'right' }}>{l.quantidadeRetornada}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>
                    {ehColetavel(l) ? emCampo(l) : 0}
                    {Number(l.emViagem) > 0 && <span style={{ fontSize: 11, color: BD.grayText }}> ({l.emViagem} em viagem)</span>}
                    {!ehColetavel(l) && emCampo(l) > 0 && <div style={{ fontSize: 11, color: SEVERIDADE.critico.texto }}>não retorna ({(CICLO_UI[l.cicloVida as CicloVida]?.label ?? String(l.cicloVida)).toLowerCase()})</div>}
                  </td>
                  <td style={{ fontSize: 13 }}>{l.condicaoRetorno ? rotuloDecisao('RETORNO', l.condicaoRetorno) : ''}{l.observacaoRetorno ? ` · ${l.observacaoRetorno}` : ''}</td>
                </tr>
              ))}
              {r.itens.every((l) => l.unidadeId == null) && <tr><td colSpan={6} style={{ color: BD.grayText, padding: 12 }}>Esta remessa não levou equipamento serializado: não há coleta nem conferência.</td></tr>}
            </tbody>
          </Table>
        </TabelaWrap>

        <ListaCartoes>
          {r.itens.filter((l) => l.unidadeId != null).map((l) => (
            <Card key={l.id} style={{ padding: 12 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <b style={{ flexGrow: 1, fontSize: 15 }}>{l.itemNome}</b>
                <span style={{ fontFamily: 'monospace', fontSize: 12, color: BD.grayText }}>{l.serial ?? '—'}</span>
              </div>
              <div style={{ marginTop: 4, fontSize: 13, color: BD.grayText }}>
                saiu {l.quantidade} · voltou {l.quantidadeRetornada} · em campo <b>{ehColetavel(l) ? emCampo(l) : 0}</b>
                {Number(l.emViagem) > 0 ? ` (${l.emViagem} em viagem)` : ''}
              </div>
              {!ehColetavel(l) && emCampo(l) > 0 && <div style={{ marginTop: 4, fontSize: 12, color: SEVERIDADE.critico.texto }}>não retorna ({(CICLO_UI[l.cicloVida as CicloVida]?.label ?? String(l.cicloVida)).toLowerCase()})</div>}
              {(l.condicaoRetorno || l.observacaoRetorno) && (
                <div style={{ marginTop: 4, fontSize: 12, color: BD.grayText }}>{l.condicaoRetorno ? rotuloDecisao('RETORNO', l.condicaoRetorno) : ''}{l.observacaoRetorno ? ` · ${l.observacaoRetorno}` : ''}</div>
              )}
            </Card>
          ))}
          {r.itens.every((l) => l.unidadeId == null) && (
            <Card style={{ padding: 12, color: BD.grayText }}>Esta remessa não levou equipamento serializado: não há coleta nem conferência.</Card>
          )}
        </ListaCartoes>
        {perdidas.length > 0 && (
          <div style={{ fontSize: 12, color: BD.grayText, marginTop: 8 }}>
            Peça declarada na conferência como não retornada sai da coleta: o caminho dela é a fila de baixa.
          </div>
        )}
      </Bloco>

      {(fotosSeparacao.length > 0 || comprovanteDespacho || comprovanteEntrega || r.entregueEm) && (
        <Bloco titulo="Provas da saída e da entrega">
          <div style={{ display: 'grid', gap: 8, fontSize: 13 }}>
            {fotosSeparacao.length > 0 && (
              <div>
                <span style={{ color: BD.grayText }}>Fotos da separação:</span>{' '}
                {fotosSeparacao.map((a, k) => (
                  <span key={a.id}>{k > 0 ? ' · ' : ''}<a href={a.url} target="_blank" rel="noreferrer">{a.nome}</a> <span style={{ color: BD.grayText }}>({fmtDateTime(a.enviadoEm)})</span></span>
                ))}
              </div>
            )}
            <div>
              <span style={{ color: BD.grayText }}>Comprovante de despacho:</span>{' '}
              {comprovanteDespacho ? <a href={comprovanteDespacho.url} target="_blank" rel="noreferrer">{comprovanteDespacho.nome}</a> : r.comprovanteDespachoUrl ? 'registrado' : '—'}
            </div>
            {r.entregueEm && (
              <>
                <div>
                  <span style={{ color: BD.grayText }}>Recebido por:</span> {r.recebidoPor || 'não informado'}
                  {r.recebidoDocumento ? ` · ${exibirDocumento(r.recebidoDocumento)}` : ''}
                </div>
                <div>
                  <span style={{ color: BD.grayText }}>Comprovante de entrega:</span>{' '}
                  {comprovanteEntrega ? <a href={comprovanteEntrega.url} target="_blank" rel="noreferrer">{comprovanteEntrega.nome}</a> : r.comprovanteEntregaUrl ? 'registrado' : 'não enviado'}
                </div>
              </>
            )}
          </div>
        </Bloco>
      )}

      {consumo.length > 0 && (
        <Bloco titulo={`Consumo enviado · ${consumo.reduce((a, l) => a + Number(l.quantidade), 0)}`}>
          <div style={{ fontSize: 13, color: BD.grayText, marginBottom: 8 }}>
            Material que saiu do patrimônio na entrega. Não volta e não se confere — por isso não aparece na coleta.
          </div>
          <TabelaWrap>
            <Table>
              <thead><tr><th>Item</th><th style={{ textAlign: 'right' }}>Enviado</th></tr></thead>
              <tbody>{consumo.map((l) => <tr key={l.id}><td>{l.itemNome}</td><td style={{ textAlign: 'right' }}>{l.quantidade}</td></tr>)}</tbody>
            </Table>
          </TabelaWrap>
          <ListaCartoes>
            {consumo.map((l) => (
              <Card key={l.id} style={{ padding: 12, display: 'flex', gap: 8 }}>
                <span style={{ flexGrow: 1, fontSize: 15 }}>{l.itemNome}</span>
                <b>{l.quantidade}</b>
              </Card>
            ))}
          </ListaCartoes>
        </Bloco>
      )}

      {!age && acao && <Card style={{ padding: 12, marginBottom: 16, color: BD.grayText }}>Agir nesta remessa exige a permissão "Remessas (entrega, coleta e conferência)".</Card>}

      {age && acao === 'ENTREGA' && (
        <Bloco titulo="Confirmar chegada">
          <div style={{ fontSize: 13, color: BD.grayText, marginBottom: 8 }}>A data da entrega é a que conta o contrato: informe quando chegou, não quando você está registrando.</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            <Field><label>Quando chegou</label><Input type="date" max={hoje} value={entrega.entregueEm} onChange={(e) => setEntrega({ ...entrega, entregueEm: e.target.value })} /></Field>
            <Field><label>Quem recebeu</label><Input value={entrega.recebidoPor} maxLength={150} onChange={(e) => setEntrega({ ...entrega, recebidoPor: e.target.value })} /></Field>
            <Field><label>CPF ou CNPJ de quem recebeu (opcional)</label><Input value={entrega.recebidoDocumento} maxLength={18} onChange={(e) => setEntrega({ ...entrega, recebidoDocumento: mascaraDocumento(e.target.value) })} /></Field>
            <Field>
              <label>Comprovante (opcional)</label>
              {!podeAnexar
                ? <div style={{ fontSize: 13, color: BD.grayText }}>Anexar exige a permissão "Anexos". A entrega pode ser confirmada sem comprovante.</div>
                : entrega.comprovanteKey ? (
                  <div style={{ fontSize: 13, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    Comprovante enviado.
                    <Button $variant="secondary" disabled={busy} onClick={trocarComprovante}>Trocar</Button>
                  </div>
                ) : <AnexarArquivo aoEnviar={enviarComprovante} disabled={busy} />}
            </Field>
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
            <SoDesktop>
              <Button disabled={busy || !!ACOES.ENTREGA.impedimento} onClick={confirmarEntrega}>Confirmar chegada</Button>
            </SoDesktop>
            {ACOES.ENTREGA.impedimento && <span style={{ fontSize: 13, color: BD.grayText }}>{ACOES.ENTREGA.impedimento}</span>}
          </div>
        </Bloco>
      )}

      {pode('ATENDER') && podeCancelarRemessa(status, r.solicitacaoId) && (
        <Bloco titulo="Cancelar remessa">
          <div style={{ fontSize: 13, color: BD.grayText, marginBottom: 8 }}>
            Vale só antes da entrega. O equipamento volta para a praça de origem, as peças voltam separadas e o pedido volta para Pronta, para expedir de novo.
          </div>
          {confirmandoCancelar ? (
            <>
              <Field style={{ marginBottom: 8 }}>
                <label>Motivo do cancelamento</label>
                <Input aria-label="Motivo do cancelamento" aria-describedby="motivo-cancelamento-ajuda" value={motivoCancelamento} maxLength={400} onChange={(e) => setMotivoCancelamento(e.target.value)} />
                <div id="motivo-cancelamento-ajuda" style={{ fontSize: 12, color: BD.grayText, marginTop: 4 }}>
                  O solicitante vê este motivo no histórico do pedido e recebe por e-mail.
                </div>
              </Field>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button $variant="secondary" disabled={busy} onClick={desarmarCancelamento}>Voltar</Button>
                <Button $variant="secondary" disabled={busy || !!impCancelar} onClick={cancelarRemessa}>Confirmar o cancelamento de {r.codigo}</Button>
                {impCancelar && <span style={{ fontSize: 13, color: BD.grayText }}>{impCancelar}</span>}
              </div>
            </>
          ) : (
            <Button $variant="secondary" disabled={busy} onClick={() => setConfirmandoCancelar(true)}>Cancelar remessa</Button>
          )}
        </Bloco>
      )}

      {status === 'CANCELADA' && r.canceladoEm && (
        <Bloco titulo="Remessa cancelada">
          <div style={{ fontSize: 13 }}>
            {fmtDateTime(r.canceladoEm)}{r.canceladoPorNome ? ` por ${r.canceladoPorNome}` : ''}
            {r.motivoCancelamento && <div style={{ marginTop: 4, color: BD.grayText }}>Motivo: {r.motivoCancelamento}</div>}
          </div>
        </Bloco>
      )}

      {age && acao === 'COLETA' && (
        <Bloco titulo="Registrar coleta (esta viagem)">
          <div style={{ fontSize: 13, color: BD.grayText, marginBottom: 8 }}>A coleta é uma viagem, não a remessa inteira. Marque o que está voltando agora; o que ficar com o detentor precisa de nova previsão.</div>
          {pecasParaColetar.length > 0 && <LeitorContinuo titulo={`Coleta ${r.codigo}`} aoLer={lerColeta} disabled={busy} />}
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <Button $variant="secondary" onClick={() => setColeta({ ...coleta, quantidades: Object.fromEntries(paraColetar.map((l) => [l.id, emCampo(l)])) })}>Marcar todas</Button>
            <Button $variant="secondary" onClick={() => setColeta({ ...coleta, quantidades: {} })}>Desmarcar</Button>
          </div>
          <TabelaWrap>
            <Table>
              <thead><tr><th>Item</th><th>Serial</th><th style={{ textAlign: 'right' }}>Em campo</th><th>Volta agora</th></tr></thead>
              <tbody>
                {paraColetar.map((l) => (
                  <tr key={l.id}>
                    <td>{l.itemNome}</td><td style={{ fontFamily: 'monospace' }}>{l.serial ?? '—'}</td><td style={{ textAlign: 'right' }}>{emCampo(l)}</td>
                    <td>
                      {l.controle === 'SERIAL'
                        ? <input type="checkbox" checked={(coleta.quantidades[l.id] || 0) > 0} onChange={(e) => setColeta({ ...coleta, quantidades: { ...coleta.quantidades, [l.id]: e.target.checked ? 1 : 0 } })} />
                        : <Input type="number" min={0} max={emCampo(l)} style={{ width: 100 }} value={coleta.quantidades[l.id] ?? ''} onChange={(e) => setColeta({ ...coleta, quantidades: { ...coleta.quantidades, [l.id]: Number(e.target.value) || 0 } })} />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TabelaWrap>

          <ListaCartoes>
            {pecasParaColetar.length > 0 && (
              <Card style={{ padding: 12 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                  <b style={{ flexGrow: 1, fontSize: 15 }}>Peças</b>
                  <span style={{ fontSize: 12, color: BD.grayText }}>{pecasMarcadas} de {pecasParaColetar.length} voltando</span>
                </div>
                <ListaMarcacao
                  itens={pecasParaColetar.map((l) => ({
                    id: l.id,
                    titulo: l.itemNome,
                    detalhe: <span style={{ fontFamily: 'monospace' }}>{l.serial ?? '—'}</span>,
                    marcada: (coleta.quantidades[l.id] || 0) > 0,
                  }))}
                  disabled={busy}
                  aoAlternar={(id, marcada) => setColeta({ ...coleta, quantidades: { ...coleta.quantidades, [id]: marcada ? 1 : 0 } })}
                />
              </Card>
            )}
            {consumiveisParaColetar.map((l) => (
              <LinhaQuantidade
                key={l.id}
                titulo={l.itemNome}
                referencia={`${emCampo(l)} em campo`}
                valor={String(coleta.quantidades[l.id] ?? '')}
                aoMudar={(v) => setColeta({ ...coleta, quantidades: { ...coleta.quantidades, [l.id]: Number(v) || 0 } })}
                rotulo={`voltam agora: ${l.itemNome}`}
                max={emCampo(l)}
                disabled={busy}
              />
            ))}
          </ListaCartoes>
          {sobra > 0 && (
            <Field style={{ marginTop: 8 }}><label>Fica {sobra} com o detentor. Nova previsão de retorno</label><Input type="date" min={hoje} value={coleta.novaPrevisao} onChange={(e) => setColeta({ ...coleta, novaPrevisao: e.target.value })} /></Field>
          )}
          <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
            <SoDesktop>
              <Button disabled={busy || !!impColeta} onClick={registrarColeta}>Registrar coleta</Button>
            </SoDesktop>
            {impColeta && <span style={{ fontSize: 13, color: BD.grayText }}>{impColeta}</span>}
          </div>
        </Bloco>
      )}

      {age && acao === 'RECEBIMENTO' && (
        <Bloco titulo="Recebimento na praça">
          <div style={{ fontSize: 13, color: BD.grayText, marginBottom: 8 }}>O que está em viagem chegou à praça. Depois vem a conferência peça a peça.</div>
          <SoDesktop>
            <Button disabled={busy} onClick={confirmarRecebimento}>Confirmar recebimento</Button>
          </SoDesktop>
        </Bloco>
      )}

      {age && acao === 'CONFERENCIA' && (
        <Bloco titulo="Conferência peça a peça">
          <div style={{ fontSize: 13, color: BD.grayText, marginBottom: 8 }}>
            Uma decisão por peça <b>desta viagem</b>. O que ficou com o detentor continua sendo responsabilidade dele e não se confere agora; consumo não retorna. "Não voltou" lança extravio.
          </div>
          {daViagem.some((x) => x.controle === 'SERIAL') && <LeitorContinuo titulo={`Conferência ${r.codigo}`} aoLer={lerConferencia} disabled={busy} />}
          <TabelaWrap>
            <Table>
              <thead><tr><th>Item</th><th>Serial</th><th style={{ textAlign: 'right' }}>Saiu</th><th>Decisão</th><th>Voltou</th><th>Observação</th></tr></thead>
              <tbody>
                {daViagem.map((l) => {
                  const x = conf[l.id] || { decisao: '', quantidadeRetornada: '', observacao: '' }
                  return (
                    <tr key={l.id}>
                      <td>{l.itemNome}</td><td style={{ fontFamily: 'monospace' }}>{l.serial ?? '—'}</td><td style={{ textAlign: 'right' }}>{l.quantidade}</td>
                      <td>
                        <Select value={x.decisao} onChange={(e) => setLinhaConf(l, { decisao: e.target.value as DecisaoConferencia | '' })}>
                          <option value="">— decida —</option>
                          {decisoesDe('RETORNO').map((d) => <option key={d.decisao} value={d.decisao}>{d.rotulo}</option>)}
                        </Select>
                      </td>
                      <td>{l.controle === 'QUANTIDADE' && x.decisao && x.decisao !== 'NAO_RETORNOU'
                        ? <Input type="number" min={0} max={l.quantidade} style={{ width: 90 }} value={x.quantidadeRetornada} onChange={(e) => setLinhaConf(l, { quantidadeRetornada: e.target.value })} />
                        : <span style={{ color: BD.grayText }}>{x.decisao === 'NAO_RETORNOU' ? '0' : l.controle === 'SERIAL' && x.decisao ? '1' : ''}</span>}</td>
                      <td><Input value={x.observacao} maxLength={400} placeholder="opcional" onChange={(e) => setLinhaConf(l, { observacao: e.target.value })} /></td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </TabelaWrap>

          <ListaCartoes>
            {daViagem.map((l) => {
              const x = conf[l.id] || { decisao: '', quantidadeRetornada: '', observacao: '' }
              return (
                <Card key={l.id} style={{ padding: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <b style={{ flexGrow: 1, fontSize: 15 }}>{l.itemNome}</b>
                    <span style={{ fontFamily: 'monospace', fontSize: 12, color: BD.grayText }}>{l.serial ?? '—'}</span>
                  </div>
                  <div style={{ marginTop: 2, fontSize: 12, color: BD.grayText }}>saiu {l.quantidade}</div>
                  <div style={{ marginTop: 10 }}>
                    <Select value={x.decisao} onChange={(e) => setLinhaConf(l, { decisao: e.target.value as DecisaoConferencia | '' })}>
                      <option value="">— decida —</option>
                      {decisoesDe('RETORNO').map((d) => <option key={d.decisao} value={d.decisao}>{d.rotulo}</option>)}
                    </Select>
                  </div>
                  {l.controle === 'QUANTIDADE' && x.decisao && x.decisao !== 'NAO_RETORNOU' && (
                    <div style={{ marginTop: 10 }}>
                      <Stepper
                        valor={x.quantidadeRetornada}
                        aoMudar={(v) => setLinhaConf(l, { quantidadeRetornada: v })}
                        rotulo={`voltou: ${l.itemNome}`}
                        max={Number(l.quantidade)}
                      />
                    </div>
                  )}
                  <div style={{ marginTop: 10 }}>
                    <Input value={x.observacao} maxLength={400} placeholder="Observação (opcional)" onChange={(e) => setLinhaConf(l, { observacao: e.target.value })} />
                  </div>
                </Card>
              )
            })}
          </ListaCartoes>

          <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
            <SoDesktop>
              <Button disabled={busy || !!impConf} onClick={fecharConferencia}>Fechar conferência</Button>
            </SoDesktop>
            {impConf && <span style={{ fontSize: 13, color: BD.grayText }}>{impConf}</span>}
          </div>
        </Bloco>
      )}

      {desfecho && <Card style={{ padding: 12, marginBottom: 16, fontWeight: 600 }}>{desfecho}</Card>}
      {r.observacao && <Bloco titulo="Observação">{r.observacao}</Bloco>}

      {age && acao && (
        <>
          <BarraFixa>
            {ACOES[acao].impedimento && (
              <span style={{ fontSize: 12, color: BD.grayText }}>{ACOES[acao].impedimento}</span>
            )}
            <Button
              disabled={busy || !!ACOES[acao].impedimento}
              onClick={ACOES[acao].rodar}
            >
              {ROTULO_ACAO[acao]}
            </Button>
          </BarraFixa>
        </>
      )}
    </>
  )
}

export default EstoqueRemessa
